import { useTranslate } from '@tolgee/react';
import { message } from 'antd';
import { useCallback } from 'react';
import {
  createConversation,
  updateCancelOrderStatus,
  updateReturnOrderStatus,
} from 'services/api/refund';
import { toUpperCase } from 'utils/toLowerCase';
import { addFakeConversationOrderSn } from 'hooks/business/conversation';
import { useGetConversationById, useSetConversation } from 'hooks/business/lruConversation';
import { useConversationRead } from 'hooks/business/useConversationRead';

interface ContactBuyerParams {
  orderSn: string;
  region: General.UpperCaseRegion;
  externalBuyerId: string;
  externalStoreId: string;
  buyerNick: string;
  returnSn?: string;
  reverseOrderId?: string;
  conversation?: Conversation.Conversation;
  type: 'refund' | 'cancel';
  channel: General.PlatformUpperCase;
  onSuccess?: (conversation: Conversation.Conversation) => void;
}

export const useContactBuyer = () => {
  const { t } = useTranslate();
  const readConv = useConversationRead();
  const setConversation = useSetConversation();
  const getConversationById = useGetConversationById();

  const trigger = useCallback(
    async (params: ContactBuyerParams) => {
      const {
        orderSn,
        region,
        externalBuyerId,
        externalStoreId,
        buyerNick,
        returnSn = '',
        reverseOrderId = '',
        conversation,
        type,
        channel,
      } = params;
      if (!conversation) {
        message.error(t('errorMsg.会话不存在'));
        return;
      }

      let _conversation = { ...conversation };

      try {
        if (
          !_conversation ||
          !_conversation.canBeSent ||
          _conversation.talkId === '0' ||
          (!_conversation.controlStatus && _conversation.receptionId === '0')
        ) {
          _conversation = await createConversation({
            region: toUpperCase(region),
            channel,
            externalStoreId,
            externalBuyerId,
            buyerNick,
            orderId: orderSn,
            sourceAction: 'ORDER_MANAGEMENT',
          });
          const { conversationId } = _conversation;
          setConversation(conversationId, _conversation);
        }

        if (channel === 'SHOPEE') {
          addFakeConversationOrderSn(_conversation.conversationId, orderSn);
        }

        const conv = getConversationById(_conversation.conversationId);
        if (conv) {
          readConv(conv);
        }
        if (type === 'cancel') {
          updateCancelOrderStatus({
            orderId: orderSn,
            reverseOrderId,
            region,
            platform: channel,
          });
        }
        if (type === 'refund') {
          updateReturnOrderStatus({
            region,
            platform: channel,
            returnSn,
          });
        }

        return _conversation;
      } catch (err) {
        message.error(t('errorMsg.创建会话失败'));
      }
    },
    [readConv, setConversation, t, getConversationById],
  );

  return trigger;
};
